import 'react-loading-skeleton/dist/skeleton.css';
import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ParsedLocation } from '@tanstack/react-router';
import { createHead, UnheadProvider } from '@unhead/react/client';
import { createRoot } from 'react-dom/client';

import { GoogleAnalyticsScript } from './analytics/GoogleAnalyticsScript';
import { App } from './App';
import { Auth0Provider } from './auth/Auth0Provider';
import { initAuthorizedAxiosInstance } from './common/api/instances/AuthorizedAxiosInstance';
import { initAuthorizedGatewayAxiosInstance } from './common/api/instances/AuthorizedGatewayAxiosInstance';
import { ItemModel } from './common/models/ItemModel';
import { GlobalStyles } from './common/ui/GlobalStyles';
import AppSettings from './constants';
import { queryClient } from './lib/queryClient';
import { OrderModel } from './order/model/OrderModel';
import { registerScrollPreventionEventListener } from './registerScrollPreventionEventListener';
import { router } from './router';
import { beforeSend } from './sentry';

declare module '@tanstack/react-router' {
    interface Register {
        router: typeof router;
    }
    interface HistoryState {
        order?: Partial<OrderModel>;
        portId?: string;
        items?: ItemModel[];
        vesselId?: string;
        customerId?: string;
        emailAddress?: string;
        originLocation?: ParsedLocation;
        redirectOrigin?: string;
    }
}

Sentry.init({
    dsn: AppSettings.SENTRY_DSN,
    environment: AppSettings.ENV,
    integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
            maskAllText: true,
            blockAllMedia: true,
        }),
        Sentry.tanstackRouterBrowserTracingIntegration(router),
    ],
    beforeSend,
    tracesSampleRate: 0.2,
    replaysOnErrorSampleRate: 1.0,
});

registerScrollPreventionEventListener();

initAuthorizedGatewayAxiosInstance(router);
initAuthorizedAxiosInstance(router);

const rootElement = document.getElementById('root');

if (!rootElement) {
    throw new Error('Root element not found');
}

const head = createHead();

createRoot(rootElement).render(
    <>
        <GlobalStyles />
        <UnheadProvider head={head}>
            <GoogleAnalyticsScript />
            <QueryClientProvider client={queryClient}>
                <Auth0Provider>
                    <App />
                </Auth0Provider>
            </QueryClientProvider>
        </UnheadProvider>
    </>
);
